/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import Hidden from "@mui/material/Hidden";
import FormFader from "../Common/Fader/FormFader";
import Paper from "@mui/material/Paper";
import MenuItem from "@mui/material/MenuItem";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useForm } from "../../../provider/Form/FormProvider";
import Person from "../Common/Person/Person";
import { dateFieldDefault, isRequired, pastDateDefault } from "../../../services/validationRules";
import Address from "../Common/Address/Address";
import ValidatorSelectField from "../../FormFields/ValidatorSelectField";
import Contact from "../Common/Contact/Contact";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import { translate } from "../../../services/translations/translations";
import FormSubmitProps from "../../../types/FormSubmitProps";
import wizardStyle from "../wizardStyle";
import { Box } from "@mui/material";

type PersonalProps = {
  submitProps: FormSubmitProps;
};

export default function Personal({ submitProps }: PersonalProps) {
  const { values, handleSubmit } = useForm();
  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Hidden smUp>
            <Typography variant={"h4"} sx={wizardStyle.headline}>
              Persönliche Angaben
            </Typography>
          </Hidden>
          <Grid container justifyContent={"center"}>
            <Person path="client" showDiverseGenderOption />
            <Grid item xs={12} sx={wizardStyle.gridTopSpaced}>
              <FormLegend>{translate("person.birthDate.question", values)}</FormLegend>
            </Grid>
            <Grid item xs={12}>
              <ValidatorDateField
                name="client.birthDate"
                label={translate("person.birthDate.label", values)}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie Ihr Geburtsdatum an",
                  },
                  dateFieldDefault,
                  pastDateDefault,
                ]}
              />
            </Grid>
            <Address path="client.residenceAddress" />
            <Contact />
            <Grid item xs={12}>
              <FormLegend>{translate("settlement.maritalStatus.question", values)}</FormLegend>
            </Grid>
            <Grid item sm={values.maritalStatus === "married" ? 6 : 12} xs={12} sx={values.maritalStatus === "married" ? wizardStyle.gridSpace : undefined}>
              <ValidatorSelectField
                label={translate("settlement.maritalStatus.label", values)}
                name="maritalStatus"
                validators={[{ validator: isRequired, message: "Bitte geben Sie Ihren Familienstand an." }]}
              >
                <MenuItem value={"unmarried"}>{translate("settlement.maritalStatus.options.unmarried.label", values)}</MenuItem>
                <MenuItem value={"married"}>{translate("settlement.maritalStatus.options.married.label", values)}</MenuItem>
                <MenuItem value={"divorced"}>{translate("settlement.maritalStatus.options.divorced.label", values)}</MenuItem>
              </ValidatorSelectField>
            </Grid>
            {values.maritalStatus === "married" && (
              <Grid item sm={6} xs={12}>
                <ValidatorSelectField
                  label={translate("settlement.partnerIsWorking.label", values)}
                  name="partnerIsWorking"
                  validators={[
                    {
                      validator: isRequired,
                      message: "Bitte geben Sie an, ob Ihr Ehepartner berufstätig ist.",
                    },
                  ]}
                >
                  <MenuItem value={"yes"}>{translate("settlement.partnerIsWorking.options.yes.label", values)}</MenuItem>
                  <MenuItem value={"no"}>{translate("settlement.partnerIsWorking.options.no.label", values)}</MenuItem>
                </ValidatorSelectField>
              </Grid>
            )}
            <Grid item xs={12}>
              <ValidatorSelectField
                label={translate("settlement.numberChildren.label", values)}
                name="numberChildren"
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie die Anzahl unterhaltsberechtigter Kinder an.",
                  },
                ]}
              >
                <MenuItem value={"0"}>{translate("settlement.numberChildren.options.0.label", values)}</MenuItem>
                <MenuItem value={"1"}>{translate("settlement.numberChildren.options.1.label", values)}</MenuItem>
                <MenuItem value={"2"}>{translate("settlement.numberChildren.options.2.label", values)}</MenuItem>
                <MenuItem value={"3"}>{translate("settlement.numberChildren.options.3.label", values)}</MenuItem>
                <MenuItem value={"4"}>{translate("settlement.numberChildren.options.4.label", values)}</MenuItem>
                <MenuItem value={"5"}>{translate("settlement.numberChildren.options.5.label", values)}</MenuItem>
                <MenuItem value={"6"}>{translate("settlement.numberChildren.options.6.label", values)}</MenuItem>
              </ValidatorSelectField>
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
